/**
 * Inference API Hooks
 *
 * TanStack Query hooks for inference jobs and results.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listInferenceJobsApiV1InferenceJobsGet,
  createInferenceJobApiV1InferenceJobsPost,
  getInferenceJobApiV1InferenceJobsJobIdGet,
  cancelInferenceJobApiV1InferenceJobsJobIdDelete,
  listInferenceFilesApiV1InferenceJobsJobIdFilesGet,
  getInferenceFileApiV1InferenceJobsJobIdFilesFileIdGet,
  getFileUploadUrlApiV1InferenceJobsJobIdFilesUploadUrlPost,
  confirmFileUploadApiV1InferenceJobsJobIdFilesConfirmPost,
  getFileDownloadUrlApiV1InferenceJobsJobIdFilesFileIdDownloadUrlGet,
} from '@/api';
import type { InferenceJobCreate, InferenceFileCreate } from '@/api';
import { useAuthEnabled } from './useAuthQuery';

// Query keys factory
export const inferenceJobKeys = {
  all: ['inferenceJobs'] as const,
  lists: () => [...inferenceJobKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) =>
    [...inferenceJobKeys.lists(), filters] as const,
  details: () => [...inferenceJobKeys.all, 'detail'] as const,
  detail: (id: string) => [...inferenceJobKeys.details(), id] as const,
};

export const inferenceFileKeys = {
  all: ['inferenceFiles'] as const,
  lists: () => [...inferenceFileKeys.all, 'list'] as const,
  list: (jobId: string) => [...inferenceFileKeys.lists(), jobId] as const,
  details: () => [...inferenceFileKeys.all, 'detail'] as const,
  detail: (jobId: string, fileId: string) =>
    [...inferenceFileKeys.details(), jobId, fileId] as const,
};

/**
 * List all inference jobs.
 */
export function useInferenceJobs(options?: {
  filters?: { status?: string };
  enabled?: boolean;
}) {
  const { enabled: authEnabled } = useAuthEnabled(options?.enabled ?? true);

  return useQuery({
    queryKey: inferenceJobKeys.list(options?.filters),
    queryFn: async () => {
      const response = await listInferenceJobsApiV1InferenceJobsGet({
        query: options?.filters,
      });
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to fetch inference jobs'
        );
      }
      return response.data?.items ?? [];
    },
    enabled: authEnabled,
  });
}

/**
 * Get a single inference job.
 */
export function useInferenceJob(jobId: string) {
  const { enabled: authEnabled } = useAuthEnabled(Boolean(jobId));

  return useQuery({
    queryKey: inferenceJobKeys.detail(jobId),
    queryFn: async () => {
      const response = await getInferenceJobApiV1InferenceJobsJobIdGet({
        path: { job_id: jobId },
      });
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to fetch inference job'
        );
      }
      return response.data;
    },
    enabled: authEnabled,
    // Poll for in-progress jobs
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status === 'pending' || status === 'processing') {
        return 5000; // Poll every 5s while in progress
      }
      return false;
    },
  });
}

/**
 * Create a new inference job.
 */
export function useCreateInferenceJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: InferenceJobCreate) => {
      const response = await createInferenceJobApiV1InferenceJobsPost({
        body: data,
      });
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to create inference job'
        );
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inferenceJobKeys.lists() });
    },
  });
}

/**
 * Cancel an inference job.
 */
export function useCancelInferenceJob() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (jobId: string) => {
      const response = await cancelInferenceJobApiV1InferenceJobsJobIdDelete({
        path: { job_id: jobId },
      });
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to cancel inference job'
        );
      }
      return response.data;
    },
    onSuccess: (_, jobId) => {
      queryClient.invalidateQueries({
        queryKey: inferenceJobKeys.detail(jobId),
      });
      queryClient.invalidateQueries({ queryKey: inferenceJobKeys.lists() });
    },
  });
}

/**
 * List files in an inference job.
 */
export function useInferenceFiles(jobId: string) {
  const { enabled: authEnabled } = useAuthEnabled(Boolean(jobId));

  return useQuery({
    queryKey: inferenceFileKeys.list(jobId),
    queryFn: async () => {
      const response = await listInferenceFilesApiV1InferenceJobsJobIdFilesGet({
        path: { job_id: jobId },
      });
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to fetch inference files'
        );
      }
      return response.data;
    },
    enabled: authEnabled,
  });
}

/**
 * Get a single inference file with detections.
 */
export function useInferenceFile(jobId: string, fileId: string) {
  const { enabled: authEnabled } = useAuthEnabled(Boolean(jobId) && Boolean(fileId));

  return useQuery({
    queryKey: inferenceFileKeys.detail(jobId, fileId),
    queryFn: async () => {
      const response =
        await getInferenceFileApiV1InferenceJobsJobIdFilesFileIdGet({
          path: { job_id: jobId, file_id: fileId },
        });
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to fetch inference file'
        );
      }
      return response.data;
    },
    enabled: authEnabled,
  });
}

/**
 * Get presigned upload URL for inference file.
 */
export function useGetInferenceUploadUrl(jobId: string) {
  return useMutation({
    mutationFn: async (data: InferenceFileCreate) => {
      const response =
        await getFileUploadUrlApiV1InferenceJobsJobIdFilesUploadUrlPost({
          path: { job_id: jobId },
          body: data,
        });
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to get upload URL'
        );
      }
      return response.data;
    },
  });
}

/**
 * Confirm inference file upload.
 */
export function useConfirmInferenceUpload(jobId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (fileId: string) => {
      const response =
        await confirmFileUploadApiV1InferenceJobsJobIdFilesConfirmPost({
          path: { job_id: jobId },
          body: { file_id: fileId },
        });
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to confirm upload'
        );
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: inferenceFileKeys.list(jobId),
      });
      queryClient.invalidateQueries({
        queryKey: inferenceJobKeys.detail(jobId),
      });
    },
  });
}

/**
 * Get download URL for inference file.
 */
export function useInferenceFileDownloadUrl(jobId: string, fileId: string) {
  const { enabled: authEnabled } = useAuthEnabled(Boolean(jobId) && Boolean(fileId));

  return useQuery({
    queryKey: [...inferenceFileKeys.detail(jobId, fileId), 'download-url'],
    queryFn: async () => {
      const response =
        await getFileDownloadUrlApiV1InferenceJobsJobIdFilesFileIdDownloadUrlGet(
          {
            path: { job_id: jobId, file_id: fileId },
          }
        );
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to get download URL'
        );
      }
      return response.data;
    },
    enabled: authEnabled,
    staleTime: 1000 * 60 * 14, // URLs valid for 15 min, refetch at 14
  });
}
